import {
  ArrowRight,
  Target,
  CheckCircle,
  Zap,
  Activity,
  Users,
  FileText,
  Play,
  Globe,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useTrackEvent } from '../components/AnalyticsProvider';
import DemoVideoModal from '../components/DemoVideoModal';
import MarketingLayout from '../components/MarketingLayout';
import PageHero from '../components/PageHero';
import Breadcrumb from '../components/Breadcrumb';
import { PLANS, PRICING_CONFIG } from '../config/pricing';

const features = [
  {
    icon: Target,
    title: 'Give your ambition a direction',
    description:
      'Shape your vision, mission, values and measurable objectives in one guided planning workspace.',
  },
  {
    icon: Globe,
    title: 'Make informed strategic choices',
    description:
      'Explore your market and capabilities with PESTEL, Five Forces, SWOT and competitor profiles.',
  },
  {
    icon: Zap,
    title: 'Move beyond the blank page',
    description:
      'Use AI-assisted drafts and recommendations shaped by the company context you provide. Review and refine the direction.',
  },
  {
    icon: Users,
    title: 'Make ownership visible',
    description:
      'Connect your organization chart, teams and positions to the people responsible for moving work forward.',
  },
  {
    icon: Activity,
    title: 'Connect priorities to daily work',
    description:
      'Follow objectives through strategies, projects and tasks, with owners, deadlines and progress views.',
  },
  {
    icon: FileText,
    title: 'Bring the plan into the conversation',
    description:
      'Customize a visual strategic report and export a PDF to support your next leadership discussion.',
  },
];

export default function FBLandingPage() {
  const trackEvent = useTrackEvent();
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const founder = PRICING_CONFIG[PLANS.FOUNDER];
  const pro = PRICING_CONFIG[PLANS.PRO];
  const handleSignUpClick = (location: string) => {
    trackEvent({ action: 'sign_up_click', category: 'fb_landing_page', label: location });
  };
  const handleSignInClick = () => {
    trackEvent({ action: 'sign_in_click', category: 'fb_landing_page', label: 'header_cta' });
  };
  const handleDemoClick = () => {
    trackEvent({ action: 'demo_click', category: 'fb_landing_page', label: 'hero_demo' });
    setIsDemoModalOpen(true);
  };

  return (
    <MarketingLayout
      onSignInClick={handleSignInClick}
      onSignUpClick={() => handleSignUpClick('header')}
    >
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="public-container py-3">
          <Breadcrumb items={[{ label: 'Build your strategic plan' }]} />
        </div>
      </div>
      <PageHero
        badge={<span>Clarity, connected to action.</span>}
        title={
          <>
            Build the strategy.
            <br />
            <span className="text-blue-600">Give your team a way forward.</span>
          </>
        }
        subtitle="Bring your direction, business analysis, priorities and people together. Build a clear strategic plan, then connect it to the projects and tasks that move your company forward."
      >
        <Link
          to="/sign-up"
          onClick={() => handleSignUpClick('hero_primary')}
          className="public-button public-button--primary"
        >
          Start my strategic plan <ArrowRight className="h-5 w-5" />
        </Link>
        <button onClick={handleDemoClick} className="public-button public-button--secondary">
          <Play className="h-5 w-5" />
          Watch the demo
        </button>
      </PageHero>
      <div className="public-container pb-10">
        <img
          src="/images/homepage/strategy-workspace.png"
          alt="Illustrative strategy workspace linking an objective, strategy, project and tasks"
          className="w-full max-w-5xl mx-auto rounded-2xl border border-blue-100"
          fetchPriority="high"
        />
      </div>
      <section className="public-section">
        <div className="public-container">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <p className="public-eyebrow">Give busy work a bigger purpose</p>
            <h2 className="mb-5">Big goals need clear next steps.</h2>
            <p className="text-xl">
              When the plan, priorities and daily work live apart, decisions become harder than they
              need to be. Bring them into one connected workflow.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              [
                'Make the direction shared',
                'Put the vision and objectives where your team can find them and understand the thinking behind your priorities.',
              ],
              [
                'Make the next move specific',
                'Translate selected strategies into projects and tasks with clear owners and deadlines.',
              ],
              [
                'Make progress easier to discuss',
                'See how everyday work relates to your objectives, then use that context to decide what needs attention.',
              ],
            ].map(([title, description]) => (
              <article key={title} className="public-card p-7">
                <h3 className="mb-3">{title}</h3>
                <p className="text-gray-600">{description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
      <section className="public-cta">
        <div className="public-container text-center">
          <h2 className="mb-4">Where does your strategy need attention?</h2>
          <p className="max-w-2xl mx-auto mb-7">
            Work through 12 practical questions about your direction, alignment, accountability and
            execution. See your score, then choose your next focus.
          </p>
          <Link to="/health-check" className="public-button public-button--secondary">
            Take the strategy health check <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </section>
      <section className="public-section">
        <div className="public-container">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <p className="public-eyebrow">Your entire strategy, connected</p>
            <h2 className="mb-5">From the first idea to the work ahead.</h2>
            <p className="text-xl">
              Guided frameworks, company context and practical execution tools keep the bigger
              picture within reach.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map(({ icon: Icon, title, description }) => (
              <article key={title} className="public-card p-7">
                <Icon className="h-8 w-8 text-blue-600 mb-5" />
                <h3 className="mb-3">{title}</h3>
                <p className="text-gray-600">{description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
      <section className="public-section">
        <div className="public-container">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <p className="public-eyebrow">Choose your starting point</p>
            <h2 className="mb-5">Build the plan. Activate your team.</h2>
            <p className="text-xl">
              Start with Founder for your company’s strategic direction. Add Pro Activation when you
              want team execution and AI assistance.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            <article className="public-card public-card--white p-8">
              <p className="public-eyebrow">For your company’s direction</p>
              <h3 className="mb-4">Founder</h3>
              <div className="mb-5">
                <span className="text-4xl font-bold text-gray-900">
                  ${founder.price.launch?.amount ?? founder.price.standard.amount}
                </span>
                <span className="text-gray-600">
                  {' '}
                  USD {founder.price.launch ? 'for the first year' : '/ year'}
                </span>
                <p className="text-sm text-gray-600 mt-2">
                  {founder.price.launch
                    ? `Renews at $${founder.price.standard.amount}/year.`
                    : 'Billed annually.'}
                </p>
              </div>
              <p className="text-gray-600 mb-5">
                Define your direction, assess your business and develop a clear strategic plan.
              </p>
              <ul className="space-y-3 mb-7">
                {[
                  '1 company, 1 user',
                  'Vision, mission and values',
                  'Strategic objectives',
                  'SWOT, PESTEL and Five Forces',
                  'Strategy formulation and plan summary',
                  'Visual organization chart',
                ].map((item) => (
                  <li key={item} className="flex gap-3 text-gray-600">
                    <CheckCircle className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to="/sign-up"
                onClick={() => handleSignUpClick('pricing_founder')}
                className="public-button public-button--primary w-full"
              >
                Start with Founder <ArrowRight className="h-5 w-5" />
              </Link>
            </article>
            <article className="public-card p-8">
              <p className="public-eyebrow">Requires Founder · Minimum 5 users</p>
              <h3 className="mb-4">Pro Activation</h3>
              <div className="mb-5">
                <span className="text-4xl font-bold text-gray-900">
                  ${pro.price.launch?.amount ?? pro.price.standard.amount}
                </span>
                <span className="text-gray-600"> USD / user / month</span>
                <p className="text-sm text-gray-600 mt-2">
                  {pro.price.launch
                    ? `First-year launch price. Renews at $${pro.price.standard.amount}/user/month.`
                    : 'Billed monthly.'}
                </p>
              </div>
              <p className="text-gray-600 mb-5">
                Bring your leadership team into the work of turning strategy into action.
              </p>
              <ul className="space-y-3 mb-7">
                {[
                  'AI-assisted strategy and planning',
                  'Execution Hub with connected projects and tasks',
                  'Task delegation and accountability',
                  'Execution progress and objective tracking',
                  'Role-based visibility',
                  'Priority support',
                ].map((item) => (
                  <li key={item} className="flex gap-3 text-gray-600">
                    <CheckCircle className="h-5 w-5 text-purple-600 shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to="/sign-up"
                onClick={() => handleSignUpClick('pricing_pro')}
                className="public-button public-button--primary w-full"
              >
                Activate Pro <ArrowRight className="h-5 w-5" />
              </Link>
            </article>
          </div>
          <p className="text-center text-gray-600 mt-7 text-sm">
            Launch pricing applies to the first year only. Renewal uses the standard rates shown
            above.
          </p>
        </div>
      </section>
      <section className="public-cta">
        <div className="public-container text-center">
          <h2 className="mb-4">Give your next move a clear plan.</h2>
          <p className="max-w-2xl mx-auto mb-7">
            Create your account, bring your company’s context and start connecting the direction to
            the work ahead.
          </p>
          <Link
            to="/sign-up"
            onClick={() => handleSignUpClick('final_cta')}
            className="public-button public-button--primary"
          >
            Start my strategic plan <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </section>
      <DemoVideoModal isOpen={isDemoModalOpen} onClose={() => setIsDemoModalOpen(false)} />
    </MarketingLayout>
  );
}
